"use client";

import { useState, useContext, useCallback, useEffect } from "react";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { message } from "antd";
import { GeoFenceContext } from "./context/dropContext";
import { useRouter } from "next/navigation";
import { DigitalAsset } from "@/app/types";
import { Badge, ImageIcon, Layers } from "lucide-react";
import StandardNFT from "@/components/nft-drop-sub-components/StandardNFT";
import CompressedNFT from "@/components/nft-drop-sub-components/CompressedNFT";

const TabButton = ({ active, onClick, children, icon }: any) => (
	<button
		onClick={onClick}
		className={`flex items-center gap-2 sm:px-4 sm:py-3 px-1 py-2 rounded-xl transition-colors text-sm md:text-base ${
			active
				? "bg-mgt-green text-mgt-black"
				: "bg-[#1A1A1A] text-white hover:bg-[#2A2A2A]"
		}`}
	>
		{icon}
		{children}
	</button>
);

const DropNFT = () => {
	const { setMetaData } = useContext(GeoFenceContext);
	const { provider, login, isConnected } = usePrivyWallet();
	const { push } = useRouter();

	const [activeTab, setActiveTab] = useState("standard");
	const [selectedNFTs, setSelectedNFTs] = useState<DigitalAsset[]>([]);
	const [isHydrated, setIsHydrated] = useState(false);

	// Prevent hydration mismatch
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	useEffect(() => {
		// Selected NFTs tracked for drop flow
	}, [selectedNFTs]);
	// Handle tab switching with validation
	const handleTabSwitch = useCallback(
		(newTab: string) => {
			if (selectedNFTs.length > 0) {
				message.warning(
					"You have selected NFTs. Switching tabs will clear your selection to prevent mixing NFT types.",
					3
				);
				setSelectedNFTs([]);
			}
			setActiveTab(newTab);
		},
		[selectedNFTs.length]
	);

	// Handler for dropping NFTs
	const dropNftButton = useCallback(() => {
		message.destroy();

		if (selectedNFTs.length < 2) {
			message.warning("Please select at least 2 NFTs to create a drop");
			return;
		}

		try {
			// Check if there are mixed NFT types
			const hasStandardNFTs = selectedNFTs.some((nft) => !nft.isCompressed);
			const hasCompressedNFTs = selectedNFTs.some((nft) => nft.isCompressed);

			if (hasStandardNFTs && hasCompressedNFTs) {
				message.error(
					"You cannot mix standard and compressed NFTs in the same drop. " +
						"Please select only one type of NFT.",
					4
				);
				return;
			}

			// Determine the NFT type based on the first NFT
			const isCompressed = selectedNFTs[0]?.isCompressed || false;

			if (isCompressed) {
				message.warning(
					"Dropping compressed NFTs is experimental and may not work correctly. " +
						"Please submit a ticket in our Discord (discord.gg/UpxTdUVkKy) for assistance with cNFT drops.",
					6
				);
			}

			setMetaData(
				selectedNFTs.map((nft) => ({
					...nft.metadata_uri,
					isCompressed: isCompressed, // Use the same type for all NFTs
				}))
			);

			push("/drop-nft/details");
		} catch (error) {
			console.error("Error preparing NFT drop:", error);
			message.error("Failed to prepare NFT drop. Please try again.");
		}
	}, [selectedNFTs, setMetaData, push]);

	// Selection stats
	const selectedStandardCount = selectedNFTs.filter(
		(nft) => !nft.isCompressed
	).length;
	const selectedCompressedCount = selectedNFTs.filter(
		(nft) => nft.isCompressed
	).length;

	// Show loading state during hydration
	if (!isHydrated) {
		return (
			<div className="min-h-full bg-mgt-black pt-[30px] px-4 sm:px-3 lg:px-8">
				<div className="max-w-7xl mx-auto">
					<div className="text-center py-12">
						<div className="animate-spin size-8 border-4 border-mgt-green border-t-transparent rounded-full mx-auto mb-4" />
						<p className="text-gray-400">Loading...</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-full bg-mgt-black pt-[30px] px-4 sm:px-3 lg:px-8">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-3xl font-bold mb-4 text-white">
						Select NFTs to Drop
					</h1>
					<p className="mt-2 text-sm text-gray-500 mb-6">
						Choose NFTs from your wallet to create a drop. Select two or more to
						continue.
					</p>

					{/* Tab navigation */}
					<div className="flex flex-wrap gap-3 mb-6">
						<TabButton
							active={activeTab === "standard"}
							onClick={() => handleTabSwitch("standard")}
							icon={<ImageIcon className="w-5 h-5" />}
						>
							Standard NFTs
						</TabButton>

						<TabButton
							active={activeTab === "compressed"}
							onClick={() => handleTabSwitch("compressed")}
							icon={<Badge className="w-5 h-5" />}
						>
							Compressed NFTs
						</TabButton>
					</div>

					{/* Selection summary */}
					{selectedNFTs.length > 0 && (
						<div className="bg-[#1A1A1A] rounded-xl p-4 mb-6">
							<div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
								<div>
									<h3 className="text-white font-medium flex items-center gap-2">
										<Layers className="size-5 text-mgt-green" />
										Selected NFTs:{" "}
										<span className="text-mgt-green">
											{selectedNFTs.length}
										</span>
									</h3>
									<p className="text-gray-400 text-sm mt-1">
										{selectedStandardCount > 0 && (
											<span className="mr-3">
												{selectedStandardCount} standard
											</span>
										)}
										{selectedCompressedCount > 0 && (
											<span>{selectedCompressedCount} compressed</span>
										)}
									</p>
								</div>

								<div className="flex gap-3 w-full sm:w-auto">
									{selectedNFTs.length > 0 && (
										<button
											onClick={() => setSelectedNFTs([])}
											className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 bg-opacity-10 text-red-500 border border-red-500 hover:bg-opacity-20 transition-colors flex-1 sm:flex-none"
										>
											Clear Selection
										</button>
									)}
									<button
										onClick={dropNftButton}
										className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none
                      ${
												selectedNFTs.length > 1
													? "bg-mgt-green text-black hover:bg-opacity-90"
													: "bg-mgt-green bg-opacity-50 text-black cursor-not-allowed"
											}`}
									>
										Drop Selected NFTs
									</button>
								</div>
							</div>
						</div>
					)}
				</div>

				{activeTab === "standard" ? (
					<StandardNFT
						selectedNFTs={selectedNFTs}
						setSelectedNFTs={setSelectedNFTs}
					/>
				) : (
					<CompressedNFT
						selectedNFTs={selectedNFTs}
						setSelectedNFTs={setSelectedNFTs}
					/>
				)}

				{!provider && (
					<div className="bg-[#1A1A1A] rounded-xl p-6 mt-8 text-center">
						<h2 className="text-xl font-semibold text-white mb-3">
							Connect Your Wallet
						</h2>
						<p className="text-gray-400 mb-6">
							You need to connect your wallet to access your NFTs
						</p>
						<button
							className="bg-mgt-green py-3 px-6 rounded-xl text-black font-medium hover:bg-opacity-90 transition-colors"
							onClick={() => login()}
						>
							Connect Wallet
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default DropNFT;
